import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";

const PAGES = {
  terms: {
    title: "Terms & Conditions",
    intro:
      "These terms govern your use of the LuviaRx website and your purchase of research compounds. By using this site, you agree to these terms in full.",
    sections: [
      ["Eligibility", "You must be 21 years of age or older and a qualified researcher, laboratory professional, or institutional purchaser using compounds strictly for lawful research purposes."],
      ["Research Use Only", "All products sold by LuviaRx are intended exclusively for laboratory and scientific research purposes. They are not drugs, supplements, food, or cosmetics. They are not for human consumption."],
      ["Product Information", "Compound information provided on this website is for laboratory reference only. We make no representations regarding suitability for any specific application beyond in-vitro research."],
      ["Pricing & Availability", "Prices are listed in USD and may change without notice. Availability of any compound is subject to inventory and verification."],
      ["Account & Conduct", "You are responsible for the accuracy of any information you submit and for compliance with all applicable laws and institutional policies governing your research."],
      ["Limitation of Liability", "LuviaRx is not liable for any loss or damage arising from improper handling, misuse, or use of any compound outside of qualified laboratory environments."],
      ["Changes", "We may update these terms at any time. Continued use of the site constitutes acceptance of the revised terms."],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro:
      "This policy describes the limited information LuviaRx collects, how we use it, and the controls available to you.",
    sections: [
      ["Information We Collect", "We collect information you submit at checkout (such as shipping and contact details) and basic technical information (such as device and browser metadata) to operate the site."],
      ["How We Use Information", "We use information to fulfill orders, communicate about your purchase, prevent fraud, and improve the website experience. We do not sell personal information."],
      ["Cookies", "We use a small number of cookies for essential functionality such as maintaining your cart and recording your age-acknowledgment."],
      ["Third Parties", "We rely on trusted processors (for example, payment providers) who handle data necessary to complete transactions. They are bound to protect your data."],
      ["Your Choices", "You may request a copy or deletion of your information by contacting support@luviarx.com."],
      ["Security", "We apply reasonable safeguards to protect your data; no method of transmission is, however, 100% secure."],
    ],
  },
  refund: {
    title: "Refund Policy",
    intro:
      "Because LuviaRx ships sensitive research-grade material, refunds are handled carefully and on a case-by-case basis.",
    sections: [
      ["General Policy", "All sales are generally final once a research compound has shipped. We do not accept returns of opened or used research material."],
      ["Damaged or Defective Items", "If a compound arrives damaged or with verified quality issues, contact support@luviarx.com within 7 days of delivery for review and possible replacement."],
      ["Order Cancellation", "Orders may be cancelled prior to shipment. Once an order has been dispatched, it cannot be cancelled."],
      ["Refund Process", "Approved refunds are issued to the original payment method within 5–10 business days of approval."],
    ],
  },
  shipping: {
    title: "Shipping Policy",
    intro:
      "Research compounds require careful, time-aware logistics. Our shipping is designed to preserve compound integrity from lab to lab.",
    sections: [
      ["Processing Time", "Most orders are processed and dispatched within 1–3 business days, subject to verification."],
      ["Cold-Chain Handling", "Compounds requiring cold storage are shipped with appropriate insulation. Recipients should refrigerate or freeze on arrival per the storage specifications."],
      ["Shipping Methods", "We use trusted carriers offering tracking, signature confirmation, and discreet packaging."],
      ["Domestic Shipping Only", "LuviaRx ships within the United States only. We do not currently ship internationally."],
      ["Delays", "Carrier delays and weather may extend transit times. We are not liable for delays outside our control."],
    ],
  },
  "research-use": {
    title: "Research Use Policy",
    intro:
      "All compounds sold on LuviaRx are intended exclusively for laboratory and scientific research purposes. This policy clarifies the limits of acceptable use.",
    sections: [
      ["Research Use Only", "Compounds are sold strictly for in-vitro investigation by qualified researchers, laboratory professionals, and institutions."],
      ["Prohibited Uses", "Compounds may not be administered to humans or animals; used for diagnostic, therapeutic, or cosmetic purposes; or resold for any non-research application."],
      ["Researcher Responsibility", "You are responsible for ensuring your use complies with all applicable laws, institutional policies, and ethical guidelines."],
      ["Refusal of Service", "LuviaRx reserves the right to refuse, suspend, or cancel any order at its sole discretion."],
      ["No Medical Claims", "LuviaRx makes no claims regarding safety, efficacy, or suitability of any compound for use in humans. Compounds are not FDA approved for diagnostic or therapeutic use."],
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    intro:
      "This disclaimer applies to every page of the LuviaRx website and to every product we offer for sale.",
    sections: [
      ["Not for Human Consumption", "All products are strictly for laboratory and scientific research purposes. They are not for human or animal consumption under any circumstance."],
      ["Not a Drug or Supplement", "LuviaRx products are not drugs, supplements, food, or cosmetics. They have not been evaluated by the FDA and are not approved for any diagnostic or therapeutic purpose."],
      ["No Medical Claims", "Nothing on this website should be interpreted as a medical claim. We make no representations regarding the ability of any compound to diagnose, treat, cure, or prevent any disease."],
      ["Researcher Responsibility", "Customers are solely responsible for the lawful, ethical, and safe handling of compounds purchased from LuviaRx, including compliance with all applicable laws, regulations, and institutional standards."],
      ["Research Use Only", "Use of any compound for any purpose other than qualified, in-vitro laboratory research is strictly prohibited and is at the user's sole risk and responsibility."],
      ["Limitation of Liability", "LuviaRx and its affiliates assume no liability for misuse, off-label use, or any consequence arising from use of compounds outside of qualified laboratory environments."],
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const data = PAGES[slug];
  if (!data) return <Navigate to="/" replace />;

  return (
    <main data-testid={`legal-page-${slug}`} className="py-12 md:py-20">
      <SEO
        title={data.title}
        description={data.intro}
        path={`/legal/${slug}`}
      />
      <div className="container-x max-w-3xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-charcoal-400 hover:text-charcoal-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.8} /> Back to home
        </Link>

        <p className="overline mb-3">Legal</p>
        <h1 className="font-display text-4xl md:text-6xl font-bold leading-[0.95] text-charcoal-600 mb-6">
          {data.title}
        </h1>
        <p className="text-charcoal-400 text-lg leading-relaxed mb-12">{data.intro}</p>

        <div className="space-y-10">
          {data.sections.map(([heading, body]) => (
            <section key={heading}>
              <h2 className="font-display text-2xl font-bold text-charcoal-600 mb-3">{heading}</h2>
              <p className="text-charcoal-500 leading-relaxed">{body}</p>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-charcoal-100">
          <p className="text-xs text-charcoal-300 leading-relaxed">
            For Research Use Only. Not for Human Consumption. Not intended to diagnose, treat, cure, or prevent any disease. Not FDA approved for diagnostic or therapeutic use.
          </p>
        </div>
      </div>
    </main>
  );
}
